import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaArrowRight } from "react-icons/fa"; // Ensure react-icons/fa is installed

gsap.registerPlugin(ScrollTrigger);

const WhoWeAreSection = () => {
    const sectionRef = useRef(null);
    
    // Refs for animations
    const leftContentRefs = useRef([]); // Left column text blocks
    const cardRef = useRef(null);       // The right card container
    const cardImageRef = useRef(null);  // The background image
    const cardTextRefs = useRef([]);    // Philosophy title and description
    const listItemsRefs = useRef([]);   // The list items

    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 75%", // Triggers when top of section is 75% down the viewport
                    toggleActions: "play none none none",
                }
            });

            // --- 1. Left Column: Text Cascade ---
            tl.from(leftContentRefs.current, {
                y: 40,
                opacity: 0,
                duration: 0.6,
                stagger: 0.15, // Smooth delay between paragraphs
                ease: "power2.out"
            });

            // --- 2. Right Column: Card Slide In ---
            tl.from(cardRef.current, {
                x: 50, // Slide from right
                opacity: 0,
                duration: 0.8,
                ease: "power2.out"
            }, "-=0.6"); // Overlap with left column animation

            // --- 3. Right Column: Cinematic Image Zoom ---
            // This runs at the same time as the card slide-in ("<")
            tl.from(cardImageRef.current, {
                scale: 1.2, // Start zoomed in
                duration: 1.5,
                ease: "power2.out"
            }, "<");

            // --- 4. Right Column: Text Content ---
            tl.from(cardTextRefs.current, {
                y: 20,
                opacity: 0,
                stagger: 0.1,
                duration: 0.5
            }, "-=0.8");

            // --- 5. Right Column: List Items Stagger ---
            tl.from(listItemsRefs.current, {
                x: 20,
                opacity: 0,
                stagger: 0.15, // Distinct pop for each item
                duration: 0.5,
                ease: "back.out(1.2)" // Subtle bounce
            }, "-=0.3");

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    // Helper to add refs safely
    const addToLeftRefs = (el) => { if (el && !leftContentRefs.current.includes(el)) leftContentRefs.current.push(el); };
    const addToCardTextRefs = (el) => { if (el && !cardTextRefs.current.includes(el)) cardTextRefs.current.push(el); };
    const addToListRefs = (el) => { if (el && !listItemsRefs.current.includes(el)) listItemsRefs.current.push(el); };

    return (
        <section ref={sectionRef} className="container max-w-7xl mx-auto px-6 md:px-12 py-[100px]" id='know-more'>
            <div className="flex flex-row max-md:flex-wrap-reverse gap-12">

                {/* Left Column: Text Content */}
                <div className="w-full space-y-10">
                    {/* Who We Are Section */}
                    <div>
                        <h2 ref={addToLeftRefs} className="text-[37px] font-[600] text-[#111827] leading-[30px] -tracking-[1.2px] mb-7">
                            Who We Are
                        </h2>
                        <p ref={addToLeftRefs} className="text-black font-[500] text-[18px] leading-[28px] mb-4">
                            TurningIdeas is a startup studio and venture builder founded by operators, not consultants. We exist to make entrepreneurship execution-first, by offering founders a hands-on partner to turn validated ideas into fundable, scalable ventures.
                        </p>
                        <p ref={addToLeftRefs} className="text-black font-[500] text-[18px] leading-[28px]">
                            Our team brings together product builders, growth marketers, and capital advisors who work shoulder-to-shoulder with startup founders from across the globe.
                        </p>
                    </div>

                    {/* Our Mission Section */}
                    <div>
                        <h2 ref={addToLeftRefs} className="text-[37px] font-[600] text-[#111827] leading-[30px] -tracking-[1.2px] mb-7">
                            Our Mission
                        </h2>
                        <p ref={addToLeftRefs} className="text-black font-[500] text-[18px] leading-[28px] mb-4">
                            We believe founders should not build alone. Our mission is to empower global and Indian startups by providing the right mix of strategy, execution, and ecosystem access to fast-track growth in India and Asia.
                        </p>
                        <p ref={addToLeftRefs} className="text-black font-[500] text-[18px] leading-[28px]">
                            We co-build where others advise. We prototype, launch, test, and iterate with our founders — and we stay through the journey.
                        </p>
                    </div>

                    {/* CTA Button */}
                    <div ref={addToLeftRefs}>
                        <a href='https://forms.gle/Brpzz6dAtLtcTYc9A' target='_blank' rel="noreferrer" className="inline-block bg-[#EA580C] hover:bg-orange-500 text-white py-3 px-8 rounded-full font-[600] transition-colors duration-300 text-sm">
                            Apply Now
                        </a>
                    </div>
                </div>

                {/* Right Column: Philosophy Card */}
                <div className="w-full">
                    {/* Ref attached to the main card container */}
                    <div ref={cardRef} className="relative rounded-3xl overflow-hidden shadow-2xl h-full">
                        
                        {/* Background Image */}
                        <div className="absolute inset-0">
                            <img
                                ref={cardImageRef} // Ref for Zoom Effect
                                src="img/who-we-are.png"
                                alt="Startup Philosophy Background"
                                className="w-full h-full object-cover transform scale-100 brightness-[0.5]" // base class
                            />
                            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-900/40 to-blue-900/80 mix-blend-multiply"></div>
                        </div>

                        {/* Card Content */}
                        <div className="relative z-10 p-8 lg:p-12 text-white flex flex-col justify-end h-full min-h-[500px]">
                            <h3 ref={addToCardTextRefs} className="text-2xl lg:text-3xl font-bold mb-4">
                                Our Startup Philosophy
                            </h3>
                            <p ref={addToCardTextRefs} className="text-blue-100 mb-8 leading-relaxed">
                                Every startup needs more than just funding — it needs execution firepower. We call this approach: Venture Building with Skin in the Game
                            </p>

                            <div className="space-y-4">
                                <h4 ref={addToCardTextRefs} className="font-semibold text-lg">At TurningIdeas:</h4>
                                <ul className="space-y-3">
                                    <li ref={addToListRefs} className="flex items-start">
                                        <FaArrowRight className="h-6 w-6 p-1.5 bg-[#EAB308] rounded-full text-white mr-3 flex-shrink-0" />
                                        <span className="text-blue-100">We help build your MVP, not just a pitch deck</span>
                                    </li>
                                    <li ref={addToListRefs} className="flex items-start">
                                        <FaArrowRight className="h-6 w-6 p-1.5 bg-[#EAB308] rounded-full text-white mr-3 flex-shrink-0" />
                                        <span className="text-blue-100">We validate your market, not just the idea</span>
                                    </li>
                                    <li ref={addToListRefs} className="flex items-start">
                                        <FaArrowRight className="h-6 w-6 p-1.5 bg-[#EAB308] rounded-full text-white mr-3 flex-shrink-0" />
                                        <span className="text-blue-100">We help run sales and partnerships, not just strategy sessions</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default WhoWeAreSection;